/**
 * Quality Command Center — PURE evidence-classification logic (no I/O, no Supabase).
 *
 * These functions encode the non-negotiable honesty invariants so they can be unit
 * tested in isolation:
 *   - Any unrecognised / empty / malformed result string degrades to UNKNOWN — never PASS.
 *   - A readiness level is READY only when EVERY required category is explicitly PASS;
 *     a missing category is treated as NOT_EXECUTED, never as a pass.
 */

/** The 8 canonical result classes — kept mutually distinct (product ≠ harness, blocked ≠ failed, unknown ≠ not-executed). */
export type QaResult =
  | 'PASS' | 'FAIL_PRODUCT' | 'FAIL_HARNESS' | 'BLOCKED_ENV'
  | 'BLOCKED_PRODUCT' | 'NOT_EXECUTED' | 'UNKNOWN' | 'ARTIFACT_ERROR';

export const QA_RESULT_CLASSES: QaResult[] = [
  'PASS', 'FAIL_PRODUCT', 'FAIL_HARNESS', 'BLOCKED_ENV',
  'BLOCKED_PRODUCT', 'NOT_EXECUTED', 'UNKNOWN', 'ARTIFACT_ERROR',
];

/** Normalise any raw value to a canonical class. Unknown/empty/malformed → UNKNOWN (never PASS). */
export function normResult(raw: unknown): QaResult {
  const s = String(raw ?? '').toUpperCase().replace(/[\s-]+/g, '_');
  if ((QA_RESULT_CLASSES as string[]).includes(s)) return s as QaResult;
  if (s === 'FAIL' || s === 'FAILED') return 'FAIL_PRODUCT';
  if (s === 'BLOCKED') return 'BLOCKED_ENV';
  return 'UNKNOWN';
}

/* -------------------------------------------------- artifact health */

/** Map an ingested artifact-health summary to a canonical class. Malformed → ARTIFACT_ERROR,
 *  missing → NOT_EXECUTED (incomplete evidence, never a pass), else PASS. */
export function artifactHealthClass(h?: { status?: string; missing?: string[]; malformed?: string[] } | null): QaResult {
  if (!h || !h.status) return 'UNKNOWN';
  if ((h.malformed?.length ?? 0) > 0 || h.status === 'ARTIFACT_ERROR') return 'ARTIFACT_ERROR';
  if ((h.missing?.length ?? 0) > 0 || h.status === 'INCOMPLETE') return 'NOT_EXECUTED';
  return 'PASS';
}

/* -------------------------------------------------- profile labelling */

/** Human profile label + whether it is a full-depth profile. Smoke/small are explicitly not full. */
export function profileLabel(engine: string, scenario?: string | null): { label: string; full: boolean } {
  const s = (scenario ?? '').toLowerCase();
  if (engine === 'soak') return { label: s.includes('smoke') || !s ? 'smoke (25 iterations)' : s, full: false };
  if (engine === 'performance') return { label: 'small (10 shops / 500 history rows)', full: false };
  if (engine === 'simulation') return { label: s.includes('ai') ? 'ai-mode' : 'policy-mode shop day', full: false };
  return { label: scenario || engine, full: true };
}

/* -------------------------------------------------- simulation fidelity */

export type SimFidelity = 'REAL BROWSER FLOW' | 'REAL RPC FLOW' | 'DIRECT DATABASE FLOW' | 'POLICY-MODEL FLOW' | 'SIMULATED EVENT' | 'NOT EXECUTED';

/** Classify a simulation event's fidelity. Policy-mode DB steps are DIRECT DATABASE FLOW — never
 *  equated to a real browser/OptiPlace/manager workflow. */
export function simFidelity(ev: { kind?: string; label?: string } | null | undefined): SimFidelity {
  if (!ev) return 'NOT EXECUTED';
  if (ev.kind === 'sim-step') return 'DIRECT DATABASE FLOW';
  return 'SIMULATED EVENT';
}

/* -------------------------------------------------- security coverage matrix */

export const SECURITY_SURFACES = ['UI', 'REST', 'RPC', 'RLS', 'Realtime', 'Storage', 'Session/JWT'] as const;
export const SECURITY_ATTACKS = [
  'cross-shop read', 'cross-shop write', 'direct RPC invocation', 'role escalation',
  'stale membership', 'expired session', 'forged JWT', 'anonymous access',
  'storage access', 'realtime isolation', 'SECURITY DEFINER',
] as const;

/** Map a security scenario name to one or more (surface, attack) cells. Data-driven so it is unit-testable. */
const SECURITY_CELL_MAP: { test: RegExp; surface: string; attack: string }[] = [
  { test: /tenant\.parent|own-only/i, surface: 'RLS', attack: 'cross-shop read' },
  { test: /partition direct REST/i, surface: 'REST', attack: 'cross-shop read' },
  { test: /SET-ROLE/i, surface: 'RLS', attack: 'cross-shop read' },
  { test: /rpc\.cross-shop|kpi_shop_load/i, surface: 'RPC', attack: 'direct RPC invocation' },
  { test: /role\.technician|cannot INSERT/i, surface: 'RLS', attack: 'role escalation' },
  { test: /no-membership/i, surface: 'Session/JWT', attack: 'stale membership' },
  { test: /anonymous/i, surface: 'REST', attack: 'anonymous access' },
  { test: /secdef|SECURITY DEFINER/i, surface: 'RPC', attack: 'SECURITY DEFINER' },
  { test: /realtime.*broadcast|broadcast isolation/i, surface: 'Realtime', attack: 'realtime isolation' },
  { test: /session\.expired|expired-token/i, surface: 'Session/JWT', attack: 'expired session' },
  { test: /stale-after-role|role-downgrade/i, surface: 'Session/JWT', attack: 'stale membership' },
  { test: /jwt\.forged|forged-unsigned/i, surface: 'Session/JWT', attack: 'forged JWT' },
  { test: /storage\.cross-shop/i, surface: 'Storage', attack: 'storage access' },
];

export interface SecurityCell { surface: string; attack: string; status: QaResult; scenario: string; runId?: string; }

/** Build the surface×attack coverage matrix from ingested security scenarios. Cells with no
 *  matching scenario are omitted (no evidence), executed cells carry their real status. An
 *  overall "all PASS" is impossible to show because NOT_EXECUTED surfaces remain visible. */
export function buildSecurityMatrix(scenarios: { name: string; status: string; run_id?: string }[]): SecurityCell[] {
  const cells: SecurityCell[] = [];
  for (const s of scenarios) {
    if (!/^security\./i.test(s.name)) continue;
    const m = SECURITY_CELL_MAP.find((c) => c.test.test(s.name));
    if (!m) continue;
    cells.push({ surface: m.surface, attack: m.attack, status: normResult(s.status), scenario: s.name, runId: s.run_id });
  }
  return cells;
}

/* -------------------------------------------------- trend comparability */

/** A comparability key: only runs with the SAME key may be plotted on one trend line
 *  (same engine + profile + environment + scale). Prevents comparing incompatible profiles. */
export function comparableKey(run: { engine: string; scenario?: string | null; environment?: string | null; metrics?: any }): string {
  const scale = run.metrics?.performance?.scale ?? run.metrics?.soak?.iterations ?? '';
  return [run.engine, run.scenario ?? '', run.environment ?? '', scale].join('|');
}

/** Group runs into comparable series; a series with <2 points is flagged insufficient. */
export function comparableSeries<T extends { engine: string; scenario?: string | null; environment?: string | null; metrics?: any }>(runs: T[]) {
  const groups = new Map<string, T[]>();
  for (const r of runs) {
    const k = comparableKey(r);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(r);
  }
  return [...groups.entries()].map(([key, items]) => ({ key, items, sufficient: items.length >= 2 }));
}

/** Readiness-level requirement matrix (mirrors docs/qa/platform-v2/readiness-policy.md). */
export const READINESS_LEVELS: { level: string; required: string[] }[] = [
  { level: 'DEVELOPMENT', required: [] },
  { level: 'INTERNAL PILOT', required: ['criticalWorkflow', 'multiUserRealtime', 'reconciliation', 'residueVerified', 'productionRefusal', 'intelligenceCorrectness', 'historyTenantIsolation', 'rbac', 'a11y'] },
  { level: 'EXTERNAL PILOT', required: ['criticalWorkflow', 'multiUserRealtime', 'reconciliation', 'residueVerified', 'productionRefusal', 'intelligenceCorrectness', 'historyTenantIsolation', 'rbac', 'a11y', 'securityAttackSurface', 'qaMutationConfidence', 'migrationSafety', 'chaosRecovery', 'workflowFuzzing', 'autonomousSimulation', 'visual', 'shopDay', 'exploratory'] },
  { level: 'GENERAL AVAILABILITY', required: ['criticalWorkflow', 'multiUserRealtime', 'reconciliation', 'residueVerified', 'productionRefusal', 'intelligenceCorrectness', 'historyTenantIsolation', 'rbac', 'a11y', 'securityAttackSurface', 'qaMutationConfidence', 'migrationSafety', 'chaosRecovery', 'workflowFuzzing', 'autonomousSimulation', 'performanceBaseline', 'soakStability', 'visual', 'shopDay', 'exploratory'] },
];

/**
 * Evaluate a readiness level from a categories map. A category absent from the map
 * is NOT_EXECUTED. `ready` is true only when every required category is PASS —
 * so missing / all-NOT_EXECUTED / blocked evidence can never render a level READY.
 */
export function evaluateLevel(required: string[], categories: Record<string, QaResult>) {
  const cats = required.map((c) => ({ c, status: normResult(categories[c] ?? 'NOT_EXECUTED') }));
  const passed = cats.filter((x) => x.status === 'PASS').length;
  const notReady = cats.filter((x) => x.status !== 'PASS');
  return {
    required: required.length,
    passed,
    missing: notReady.map((x) => `${x.c}:${x.status}`),
    ready: required.length > 0 && notReady.length === 0,
  };
}

/* -------------------------------------------------- logical-run / attempt grouping */

type AttemptRow = { run_id: string; logical_run_id?: string | null; is_authoritative?: boolean | null };

/** Collapse a run list to one authoritative row per logical run (default), or
 *  keep every historical attempt when showAll is true. Legacy rows without
 *  identity are treated as authoritative so they are never hidden. */
export function collapseAttempts<T extends AttemptRow>(runs: T[], showAll = false): T[] {
  return showAll ? runs.slice() : runs.filter((r) => r.is_authoritative !== false);
}

/** Count attempts per logical run (logical_run_id, falling back to run_id). */
export function attemptCounts<T extends AttemptRow>(runs: T[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of runs) { const k = r.logical_run_id ?? r.run_id; m.set(k, (m.get(k) ?? 0) + 1); }
  return m;
}

/* -------------------------------------------------- coverage status (Phase 3) */

/** Client-side coverage status for a (charter × persona × behaviorStyle) row. */
export function coverageRowStatus(row: { last_run_at?: string | null; open_finding_count?: number | null }, staleDays = 7): 'NEVER_TESTED' | 'STALE' | 'RECENT' {
  if (!row?.last_run_at) return 'NEVER_TESTED';
  const ageDays = (Date.now() - Date.parse(row.last_run_at)) / (24 * 60 * 60 * 1000);
  return ageDays > staleDays ? 'STALE' : 'RECENT';
}
